import { BRAND, BASE_URL, CITY_COUNT_LABEL, PROVINCES, CITIES, SERVICES, SECTORS, INSIGHTS } from "./site";

export const provBySlug = (s) => PROVINCES.find((p) => p.slug === s);
export const cityBySlug = (s) => CITIES.find((c) => c.slug === s);
export const serviceBySlug = (s) => SERVICES.find((x) => x.slug === s);
export const sectorBySlug = (s) => SECTORS.find((x) => x.slug === s);
export const articleBySlug = (s) => INSIGHTS.find((x) => x.slug === s);
export const citiesInProv = (ps) => CITIES.filter((c) => c.prov === ps);

const hash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const pick = (arr, seed) => arr[seed % arr.length];

const fill = (t, area) => t.replaceAll("{area}", area);

export const url = {
  home: () => "/",
  about: () => "/about/",
  services: () => "/services/",
  service: (s) => `/services/${s.slug}/`,
  serviceProv: (s, p) => `/services/${s.slug}/${p.slug}/`,
  serviceCity: (s, c) => `/services/${s.slug}/${c.slug}/`,
  locations: () => "/locations/",
  prov: (p) => `/locations/${p.slug}/`,
  city: (c) => `/locations/${c.prov}/${c.slug}/`,
  sectors: () => "/sectors/",
  sector: (x) => `/sectors/${x.slug}/`,
  insights: () => "/insights/",
  article: (a) => `/insights/${a.slug}/`,
  sitemap: () => "/sitemap/",
};

/* ---------- SEO meta builders ---------- */

export function metaHome() {
  return {
    title: `Security Guard Services Canada | ${BRAND.name} — 24/7 Licensed Guards Nationwide`,
    description: `${BRAND.name} is a First Nation-owned security guard company serving all Canadian provinces and ${CITY_COUNT_LABEL} cities. Uniformed guards, mobile patrol, event security, fire watch and more. 24/7 dispatch: ${BRAND.phone}.`,
  };
}
export function metaAbout() {
  return {
    title: `About Us | First Nation-Owned Security Company — ${BRAND.name}`,
    description: `Learn about ${BRAND.name}, a proudly First Nation-owned and operated security guard company providing licensed, insured security services across Canada. Call ${BRAND.phone}.`,
  };
}
export function metaServices() {
  return {
    title: `Security Services in Canada | 14 Professional Guard Services — ${BRAND.name}`,
    description: `Explore our full range of security services in Canada: uniformed security guards, mobile patrol, concierge security, construction site security, loss prevention, event security, fire watch, K9 units and more. 24/7 dispatch ${BRAND.phone}.`,
  };
}
export function metaService(s) {
  return {
    title: `${s.name} in Canada | 24/7 Licensed Guards — ${BRAND.name}`,
    description: `${s.short} Serving every province and ${CITY_COUNT_LABEL} Canadian cities. Keywords: ${s.kw.slice(0, 3).join(", ")}. Get a same-day quote: ${BRAND.phone}.`,
  };
}
export function metaServiceProvince(s, p) {
  return {
    title: `${s.name} ${p.name} | Licensed 24/7 Guards — ${BRAND.name}`,
    description: `Hire licensed ${s.kw[0]} anywhere in ${p.name}. ${BRAND.name} provides ${s.name.toLowerCase()} across ${p.name} with 24/7 dispatch, GPS-verified reporting and full insurance. Call ${BRAND.phone}.`,
  };
}
export function metaServiceCity(s, c) {
  const p = provBySlug(c.prov);
  return {
    title: `${s.name} ${c.name} | 24/7 ${p.abbr} Security Guards — ${BRAND.name}`,
    description: `Professional ${s.kw[0]} in ${c.name}, ${p.name}. ${BRAND.name} delivers licensed, insured ${s.name.toLowerCase()} across ${c.metro}. 24/7 dispatch: ${BRAND.phone}. Same-day quotes.`,
  };
}
export function metaLocations() {
  return {
    title: `Security Guard Service Areas | All Provinces & ${CITY_COUNT_LABEL} Cities — ${BRAND.name}`,
    description: `${BRAND.name} provides security guard services across Canada: Ontario, Quebec, BC, Alberta, Manitoba, Saskatchewan, the Atlantic provinces and the territories. Find security guards in your city.`,
  };
}
export function metaProvince(p) {
  return {
    title: `Security Guard Services ${p.name} | Licensed Guards — ${BRAND.name}`,
    description: `${BRAND.name} delivers licensed security guard services across ${p.name}: uniformed guards, mobile patrol, event security, construction security and more. 24/7 dispatch ${BRAND.phone}.`,
  };
}
export function metaCity(c) {
  const p = provBySlug(c.prov);
  return {
    title: `Security Guard Services ${c.name} | 24/7 Security Company — ${BRAND.name}`,
    description: `Looking for a security company in ${c.name}, ${p.abbr}? ${BRAND.name} provides licensed security guards, mobile patrol, event security and alarm response across ${c.metro}. Call ${BRAND.phone}.`,
  };
}
export function metaSectors() {
  return {
    title: `Security by Industry | Sectors We Protect — ${BRAND.name}`,
    description: `Industry-specific security guard programs for construction, retail, healthcare, education, industrial, hospitality, government, residential, logistics and energy sectors across Canada.`,
  };
}
export function metaSector(x) {
  return {
    title: `${x.name} Security Services in Canada — ${BRAND.name}`,
    description: `${x.desc} ${BRAND.name} provides licensed guards and patrols for the ${x.name.toLowerCase()} sector nationwide. 24/7 dispatch: ${BRAND.phone}.`,
  };
}
export function metaInsights() {
  return {
    title: `Security Industry Insights & Guides Canada — ${BRAND.name}`,
    description: `Expert guides on hiring security guards in Canada, provincial licensing, construction site theft prevention, fire watch requirements, event security planning and loss prevention.`,
  };
}
export function metaArticle(a) {
  return { title: `${a.title} — ${BRAND.name}`, description: a.excerpt };
}
export function metaContact() {
  return {
    title: `Contact Us | 24/7 Security Guard Dispatch — ${BRAND.name}`,
    description: `Reach ${BRAND.name} any time: call ${BRAND.phone} or email ${BRAND.email}. 24/7/365 dispatch for licensed security guard services across Canada. Same-day written quotes.`,
  };
}
export function metaSitemap() {
  return {
    title: `Sitemap | All Security Services & Locations — ${BRAND.name}`,
    description: `Complete index of every page: security services, industry sectors, provincial and city security guard pages, and insights from ${BRAND.name}.`,
  };
}

/* ---------- JSON-LD builders ---------- */

export const orgLd = () => ({
  "@context": "https://schema.org",
  "@type": "SecurityService",
  name: BRAND.name,
  url: BASE_URL || undefined,
  telephone: "+1-807-770-0243",
  email: BRAND.email,
  slogan: BRAND.tagline,
  areaServed: { "@type": "Country", name: "Canada" },
  address: { "@type": "PostalAddress", addressLocality: "Thunder Bay", addressRegion: "ON", addressCountry: "CA" },
  openingHours: "Mo-Su 00:00-24:00",
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: BRAND.name,
  url: BASE_URL || undefined,
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${BASE_URL}${it.path}`,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceLd = (s, areaName) => ({
  "@context": "https://schema.org",
  "@type": "SecurityService",
  name: `${s.name}${areaName ? ` in ${areaName}` : ""}`,
  provider: { "@type": "Organization", name: BRAND.name, telephone: "+1-807-770-0243", email: BRAND.email },
  areaServed: areaName ? { "@type": "Place", name: areaName } : { "@type": "Country", name: "Canada" },
  description: s.short,
});

export const articleLd = (a) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.title,
  description: a.excerpt,
  datePublished: a.date,
  author: { "@type": "Organization", name: BRAND.name },
  publisher: { "@type": "Organization", name: BRAND.name },
});

/* ---------- localized page content ---------- */

const OPENERS = [
  (s, c, p) => `When businesses in ${c.name} need ${s.kw[0]} they can trust, they call ${BRAND.name}. As a First Nation-owned and operated security company, we deploy licensed, insured and professionally supervised guards throughout ${c.metro}, backed by a 24/7 dispatch desk at ${BRAND.phone}.`,
  (s, c, p) => `${c.name} is ${c.desc}, and its security needs are as diverse as its economy. ${BRAND.name} provides ${s.name.toLowerCase()} across ${c.metro}, protecting people, property and reputations with licensed officers and GPS-verified reporting.`,
  (s, c, p) => `Searching for ${s.kw[0]} in ${c.name}? ${BRAND.name} staffs ${c.metro} with vetted, provincially licensed security professionals who know ${p.name} and understand the local risk landscape.`,
  (s, c, p) => `From ${c.landmarks[0]} to ${c.landmarks[1]}, organizations across ${c.name} rely on ${BRAND.name} for dependable ${s.name.toLowerCase()}. Every deployment is licensed, insured and supervised, with reports delivered after every shift.`,
];

const CLOSERS = [
  (s, c) => `Whether you need a single officer for one night in ${c.name} or a multi-year ${s.kw[0]} program across ${c.metro}, our team builds the plan around your risks and your budget. Call ${BRAND.phone} or email ${BRAND.email} for a same-day quote.`,
  (s, c) => `Getting started is simple: call ${BRAND.phone}, tell us about your ${c.name} property, and receive a written quote the same day. Most ${s.name.toLowerCase()} deployments in ${c.metro} can begin within 24 to 48 hours.`,
  (s, c) => `Every ${s.name.toLowerCase()} engagement in ${c.name} includes GPS-verified reporting, named account management and the backing of our 24/7 dispatch desk. Reach us any time at ${BRAND.phone} or ${BRAND.email}.`,
];

export function serviceCityContent(s, c) {
  const p = provBySlug(c.prov);
  const seed = hash(s.slug + c.slug);
  const opener = pick(OPENERS, seed);
  const closer = pick(CLOSERS, seed);
  return {
    h1: `${s.name} in ${c.name}, ${p.abbr}`,
    intro: [
      opener(s, c, p),
      ...s.pitch,
      `${c.name.charAt(0) + c.name.slice(1)} presents a distinct security environment. With an economy anchored by ${c.industries.join(", ")}, properties across ${c.metro} face risks ranging from after-hours break-ins and vandalism to workplace incidents and organized theft. Our ${s.kw[1] || s.kw[0]} programs are designed for exactly these conditions: visible deterrence where it counts, rapid response when it matters, and documentation your insurer and legal team can rely on.`,
      `We protect sites near ${c.landmarks[0]}, ${c.landmarks[1]} and ${c.landmarks[2]}, and everywhere else in the ${c.name} area. Because our officers live and work in the communities they protect, they bring local knowledge that outside providers simply cannot match.`,
    ],
    localH: `Local ${s.name} Coverage Across ${c.metro.replace(/^the /, "The ")}`,
    localPs: [
      `Our ${c.name} teams cover the entire ${c.metro} area, from ${c.landmarks[0]} through ${c.landmarks[2]} and the surrounding communities. Patrol routes and guard posts are mapped to local crime patterns, and our supervisors audit performance with unannounced field inspections.`,
      `Key ${c.name} sectors we protect include ${c.industries.join(", ")}. Each sector gets site-specific post orders: a ${c.industries[0]} facility in ${c.name} faces different risks than a ${c.industries[1]} operation, and our programs reflect that.`,
    ],
    whyH: `Why ${c.name} Chooses ${BRAND.name}`,
    why: [
      `Provincially licensed guards verified for ${p.name} deployments`,
      `24/7 live dispatch desk: ${BRAND.phone}`,
      "GPS-verified patrol logs and digital incident reports",
      "Comprehensive liability insurance and workers' compensation coverage",
      `Local officers who know ${c.name}, supervised by experienced field managers`,
      "First Nation-owned and operated, with community-rooted accountability",
    ],
    faqs: s.faqs.map((f) => ({ q: fill(f.q, c.name), a: fill(f.a, c.name) })),
    closing: closer(s, c),
  };
}

export function serviceProvinceContent(s, p) {
  const cities = citiesInProv(p.slug);
  return {
    h1: `${s.name} in ${p.name}`,
    intro: [
      `${BRAND.name} provides ${s.name.toLowerCase()} across ${p.name}, from ${cities[0] ? cities[0].name : "its largest centres"} to ${cities.length > 1 ? cities[cities.length - 1].name : "remote communities"}. ${p.blurb}`,
      ...s.pitch,
      `Every guard we deploy in ${p.name} holds a valid ${p.name} security licence, carries our full insurance coverage, and reports through our 24/7 dispatch desk. Whether your site is in ${cities.map((c) => c.name).slice(0, 3).join(", ")} or anywhere else in ${p.name}, you get the same standard: licensed, supervised, documented.`,
    ],
    faqs: s.faqs.map((f) => ({ q: fill(f.q, p.name), a: fill(f.a, p.name) })),
  };
}

export function cityContent(c) {
  const p = provBySlug(c.prov);
  return {
    h1: `Security Guard Services in ${c.name}, ${p.abbr}`,
    intro: [
      `${c.name} is ${c.desc}. ${BRAND.name} is proud to serve ${c.metro} with a complete range of licensed security guard services, from uniformed guards and mobile patrols to event security, fire watch and rapid alarm response.`,
      `As a First Nation-owned security company, we hire locally, train rigorously and supervise actively. Our ${c.name} clients include organizations in ${c.industries.join(", ")}, and our officers protect sites near ${c.landmarks[0]}, ${c.landmarks[1]} and ${c.landmarks[2]}.`,
      `Every engagement begins with a free risk assessment of your ${c.name} property. Call our 24/7 dispatch desk at ${BRAND.phone} or email ${BRAND.email} and receive a written, no-obligation quote the same day.`,
    ],
    faqs: [
      { q: `What security services do you offer in ${c.name}?`, a: `We offer all 14 of our core services in ${c.name}: uniformed guards, mobile patrol, concierge security, construction site security, loss prevention, event security, executive protection, industrial security, healthcare security, alarm response, K9 units, fire watch, airport and transit security, and Indigenous community patrols.` },
      { q: `How quickly can I get a security guard in ${c.name}?`, a: `For urgent needs our dispatch desk can often have a licensed guard on site in ${c.name} within hours. Planned contracts typically begin within 24 to 48 hours of quote approval.` },
      { q: `Are your ${c.name} guards licensed in ${p.name}?`, a: `Yes. Every officer deployed in ${c.name} holds a valid ${p.name} security licence, has cleared background checks, and is covered by our liability insurance and workers' compensation registration.` },
      { q: `How much do security guard services cost in ${c.name}?`, a: `Rates depend on the service, hours and risk profile. Mobile patrol programs start lower than static posts, and every quote is written and itemized. Call ${BRAND.phone} for a same-day ${c.name} quote.` },
    ],
  };
}

export function provinceContent(p) {
  const cities = citiesInProv(p.slug);
  return {
    h1: `Security Guard Services in ${p.name}`,
    intro: [
      `${p.blurb} ${BRAND.name} delivers professional, licensed security guard services across ${p.name}${cities.length ? `, including ${cities.map((c) => c.name).join(", ")}` : ""}.`,
      `Our ${p.name} programs cover uniformed security guards, mobile patrol, construction site security, event security, loss prevention, fire watch, alarm response and more. Every officer is licensed under ${p.name} regulations, insured, and supervised through our 24/7 dispatch desk.`,
      `Call ${BRAND.phone} or email ${BRAND.email} for a same-day quote anywhere in ${p.name}.`,
    ],
    faqs: [
      { q: `Which ${p.name} cities do you serve?`, a: `We serve ${cities.map((c) => c.name).join(", ")}${cities.length ? ", " : ""}and surrounding communities across ${p.name}. Remote and northern deployments are available on rotation.` },
      { q: `Are your guards licensed to work in ${p.name}?`, a: `Yes. Licensing is verified before every deployment, and we maintain automated tracking so no officer ever works an expired ${p.name} credential.` },
      { q: `Can you handle multi-site contracts across ${p.name}?`, a: `Absolutely. Multi-site programs with consolidated reporting and a single account manager are one of our specialties across ${p.name}.` },
    ],
  };
}

export const POPULAR_SEARCHES = [
  "security guard services",
  "security company",
  "24/7 security guards",
  "mobile patrol security",
  "construction site security",
  "event security guards",
  "concierge security",
  "retail loss prevention",
  "fire watch services",
  "alarm response",
  "licensed security guards",
  "hire security guards",
];
